/**
 * Smart Notify Step — Advanced notifications with badges and actions
 *
 * Creates interactive notifications with:
 * - Priority badges (Nuova email importante, Pubblicità, etc.)
 * - Action buttons (Create client, Dismiss, etc.)
 * - Rich metadata
 *
 * Automatically pushes to the notifications store so they appear in the bell icon.
 *
 * Config:
 * - title: string
 * - message: string
 * - badge?: string — Badge text (e.g., "Nuova email importante")
 * - priority?: 'low' | 'normal' | 'high'
 * - icon?: string
 * - actions?: Array<{ id, label, variant, handler, payload }>
 * - metadata?: Record<string, unknown>
 * - skipStore?: boolean — If true, don't push to store (for testing)
 */

import type {
  EngineStep,
  EngineExecutionContext,
  StepExecutionResult,
  StepExecutor,
  InAppNotification,
  NotificationAction,
} from '../types';

export const smartNotifyStep: StepExecutor = {
  type: 'smart_notify',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const title = (step.config.title as string) || step.name;
      const message = (step.config.message as string) || '';
      const badge = step.config.badge as string | undefined;
      const priority = (step.config.priority as 'low' | 'normal' | 'high') || 'normal';
      const icon = step.config.icon as string | undefined;
      const actions = step.config.actions as NotificationAction[] | undefined;
      const metadata = step.config.metadata as Record<string, unknown> | undefined;
      const notifType = (step.config.notifType as InAppNotification['type']) || 'new_email';
      const skipStore = step.config.skipStore as boolean | undefined;

      const notification: InAppNotification = {
        id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: notifType,
        title,
        message,
        entityType: context.triggerEvent.entityType,
        entityId: context.triggerEvent.entityId,
        read: false,
        createdAt: new Date().toISOString(),
        module: 'email',
        icon,
        priority,
        badge,
        actions,
        metadata: {
          ...metadata,
          workflowId: context.workflow.id,
          executionId: context.executionId,
        },
      };

      // Push to notifications store so it appears in the bell icon
      if (!skipStore) {
        try {
          const { useNotificationsStore } = await import('../../../stores/notificationsStore');
          useNotificationsStore.getState().addNotification(notification);
          console.log('[SmartNotify] Notification pushed to store:', notification.badge || notification.title);
        } catch (storeErr) {
          console.warn('[SmartNotify] Could not push to store:', storeErr);
        }
      }

      return {
        success: true,
        output: { notification, notificationId: notification.id },
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Smart notify step failed',
      };
    }
  },
};
